import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ticketService } from '../../api/ticketService';
import { getAssignableUsers, getUserDisplayName, isTicketHolderRole } from '../../api/userManagementService';
import inquiryTypeMasterService from '../../api/inquiryTypeMasterService';
import inquiryCategoryMasterService from '../../api/inquiryCategoryMasterService';
import inquirySubCategoryMasterService from '../../api/inquirySubCategoryMasterService';
import inquiryEscalationLevelMasterService from '../../api/inquiryEscalationLevelMasterService';
import { registrationService } from '../../api/registrationService';
import { useStatuses } from '../../hooks/useStatuses';
import { usePriorities } from '../../hooks/usePriorities';
import { useCategories } from '../../hooks/useCategories';
import { extractErrorMessage } from '../../utils/errorHandler';
import { excludeSuperAdminTickets } from '../../utils/reportFilters';
import { formatTicketTagsLabel } from '../../utils/ticketTagDisplay';
import {
  masterRows,
  optionValue,
  resolveTicketCategoryLabel,
  resolveTicketPriorityLabel,
  resolveCurrentSlaWindow,
} from '../../utils/inquiryDisplay';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { formatDateTime } from '../../components/globaldeclaration';
import { 
  Ticket, 
  Search, 
  Filter, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  Download,
  ChevronDown,
  FileText,
  BarChart3,
  Eye
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const getRequesterDisplayName = (ticket) =>
  ticket?.requesterFullName ||
  ticket?.requesterName ||
  ticket?.requester?.fullName ||
  ticket?.createdByName ||
  'N/A';

/** Split formatted datetime onto two lines so columns don't overflow into Action. */
const renderDateTimeCell = (value, emptyLabel = '') => {
  const formatted = formatDateTime(value);
  if (!formatted || formatted === '-') return emptyLabel;
  const spaceIdx = formatted.indexOf(' ');
  if (spaceIdx < 0) {
    return <span className="leading-snug break-words">{formatted}</span>;
  }
  return (
    <div className="leading-snug break-words">
      <div>{formatted.slice(0, spaceIdx)}</div>
      <div className="text-muted-foreground">{formatted.slice(spaceIdx + 1)}</div>
    </div>
  );
};

const ListOfTicketsReport = () => {
  const navigate = useNavigate();
  const [allTickets, setAllTickets] = useState([]); // Store all fetched tickets
  const [filteredTickets, setFilteredTickets] = useState([]); // Filtered tickets for display
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Inquiry filter states
  const [selectedInquiryType, setSelectedInquiryType] = useState('all');
  const [selectedInquiryCategory, setSelectedInquiryCategory] = useState('all');
  const [selectedInquirySubCategory, setSelectedInquirySubCategory] = useState('all');
  const [selectedInquiryPriority, setSelectedInquiryPriority] = useState('all');
  const [selectedEscalationLevel, setSelectedEscalationLevel] = useState('all');
  const [selectedResponsibleRole, setSelectedResponsibleRole] = useState('all');
  const [escalatedFilter, setEscalatedFilter] = useState('all');
  const [slaBreachFilter, setSlaBreachFilter] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [ticketTags, setTicketTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Dropdown data
  const [agents, setAgents] = useState([]);
  const { data: statuses = [], isLoading: loadingStatuses } = useStatuses();
  const { data: priorities = [], isLoading: loadingPriorities } = usePriorities();
  const { data: categoriesRaw = [], isLoading: loadingCategories } = useCategories();
  const categories = useMemo(() => {
    return (Array.isArray(categoriesRaw) ? categoriesRaw : []).filter((cat) => {
      const categoryId = cat.categoryId || cat.id;
      const categoryName = cat.categoryName || cat.name || '';
      const isActive =
        cat.isActive !== false &&
        cat.status !== false &&
        String(cat.status || '').toLowerCase() !== 'inactive';
      return (
        isActive &&
        categoryId !== null &&
        categoryId !== undefined &&
        categoryName !== 'Select a category...' &&
        categoryName.trim() !== ''
      );
    });
  }, [categoriesRaw]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [chartDataLoading, setChartDataLoading] = useState(false);
  const [reportStats, setReportStats] = useState({ totalTickets: 0, byStatus: [], byPriority: [] });
  
  // Inquiry master data
  const [inquiryTypes, setInquiryTypes] = useState([]);
  const [inquiryCategories, setInquiryCategories] = useState([]);
  const [inquirySubCategories, setInquirySubCategories] = useState([]);
  const [inquiryPriorityLevels, setInquiryPriorityLevels] = useState([]);
  const [escalationLevels, setEscalationLevels] = useState([]);
  const [responsibleRoles, setResponsibleRoles] = useState([]);
  const [loadingInquiryMasters, setLoadingInquiryMasters] = useState(false);
  
  const loadTicketsRef = useRef(null);
  const loadReportStatsRef = useRef(null);
  const setChartDataLoadingRef = useRef(null);
  const ticketsFetchIdRef = useRef(0);
  const statsFetchIdRef = useRef(0);

  // Load inquiry masters on mount; agents wait for a Responsible Role (see below).
  useEffect(() => {
    loadInquiryMasters();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingTags(true);
    ticketService.getTicketTags()
      .then((resp) => {
        const raw = resp?.data?.data ?? resp?.data ?? resp;
        const rows = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        if (!cancelled) setTicketTags(rows);
      })
      .catch(() => {
        if (!cancelled) setTicketTags([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTags(false);
      });
    return () => { cancelled = true; };
  }, []);

  const loadInquiryMasters = async () => {
    setLoadingInquiryMasters(true);
    try {
      const [typesResp, categoriesResp, subCategoriesResp, priorityLevelsResp, escalationLevelsResp, rolesResp] = await Promise.all([
        inquiryTypeMasterService.getAll(true),
        inquiryCategoryMasterService.getAll(true),
        inquirySubCategoryMasterService.getAll(true),
        ticketService.getInquiryPriorityLevelsForCreate(),
        inquiryEscalationLevelMasterService.getAll(true),
        registrationService.getRoles()
      ]);
      
      setInquiryTypes(masterRows(typesResp));
      setInquiryCategories(masterRows(categoriesResp));
      setInquirySubCategories(masterRows(subCategoriesResp));
      setInquiryPriorityLevels(masterRows(priorityLevelsResp));
      setEscalationLevels(
        [...masterRows(escalationLevelsResp)].sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
      );
      setResponsibleRoles(masterRows(rolesResp).filter(isTicketHolderRole));
    } catch (error) {
      console.warn('Failed to load inquiry masters:', error);
    } finally {
      setLoadingInquiryMasters(false);
    }
  };

  /** Categories belong to a type, so narrow them once a type is picked (no extra API call). */
  const visibleInquiryCategories = useMemo(() => {
    if (selectedInquiryType === 'all') return inquiryCategories;
    return inquiryCategories.filter((c) => optionValue(c.inquiryTypeId) === selectedInquiryType);
  }, [inquiryCategories, selectedInquiryType]);

  const handleInquiryCategoryChange = async (value) => {
    setSelectedInquiryCategory(value);
    setSelectedInquirySubCategory('all');
    setCurrentPage(0);
    try {
      const rows = value === 'all'
        ? await inquirySubCategoryMasterService.getAll(true)
        : await inquirySubCategoryMasterService.getAll(true, Number(value));
      setInquirySubCategories(masterRows(rows));
    } catch (e) {
      console.warn('Failed to load inquiry sub categories:', e);
      setInquirySubCategories([]);
    }
  };

  const handleInquiryTypeChange = (value) => {
    setSelectedInquiryType(value);
    handleInquiryCategoryChange('all');
  };

  // Debounce search term to avoid too many API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load tickets when filters or pagination changes
  useEffect(() => {
    loadTickets();
  }, [currentPage, pageSize, debouncedSearchTerm, selectedAgent, selectedStatus, selectedPriority, selectedCategory, selectedSource, fromDate, toDate, selectedInquiryType, selectedInquiryCategory, selectedInquirySubCategory, selectedInquiryPriority, selectedEscalationLevel, selectedResponsibleRole, escalatedFilter, slaBreachFilter, selectedTag]);

  const buildReportFilterParams = () => {
    const params = {};
    if (selectedStatus !== 'all') {
      const selectedStatusObj = statuses.find(s => {
        const statusValue = s.statusCode || s.code || String(s.statusId || s.id);
        return statusValue === selectedStatus;
      });
      const statusCode = selectedStatusObj?.statusCode || selectedStatusObj?.code || selectedStatus;
      params.status = statusCode;
    }
    if (selectedPriority !== 'all') {
      const priorityId = parseInt(selectedPriority);
      if (!isNaN(priorityId)) params.priority = priorityId;
    }
    if (selectedCategory !== 'all') {
      const categoryId = parseInt(selectedCategory);
      if (!isNaN(categoryId)) params.categoryId = categoryId;
    }
    if (selectedAgent !== 'all') {
      const agentId = parseInt(selectedAgent);
      if (!isNaN(agentId)) params.assigneeId = agentId;
    }
    if (selectedSource !== 'all') params.source = selectedSource;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    
    // Inquiry filters
    if (selectedInquiryType !== 'all') {
      const typeId = parseInt(selectedInquiryType);
      if (!isNaN(typeId)) params.inquiryTypeId = typeId;
    }
    if (selectedInquiryCategory !== 'all') {
      const catId = parseInt(selectedInquiryCategory);
      if (!isNaN(catId)) params.inquiryCategoryId = catId;
    }
    if (selectedInquirySubCategory !== 'all') {
      const subCatId = parseInt(selectedInquirySubCategory);
      if (!isNaN(subCatId)) params.inquirySubCategoryId = subCatId;
    }
    if (selectedInquiryPriority !== 'all') {
      const priId = parseInt(selectedInquiryPriority);
      if (!isNaN(priId)) params.inquiryPriorityLevelId = priId;
    }
    if (selectedEscalationLevel !== 'all') {
      const escalationId = parseInt(selectedEscalationLevel);
      if (!isNaN(escalationId)) params.inquiryEscalationLevelId = escalationId;
    }
    if (selectedResponsibleRole !== 'all') {
      const roleId = parseInt(selectedResponsibleRole);
      if (!isNaN(roleId)) params.responsibleRoleId = roleId;
    }
    if (escalatedFilter === 'yes') params.escalated = true;
    else if (escalatedFilter === 'no') params.escalated = false;
    if (slaBreachFilter === 'yes') params.slaBreach = true;
    else if (slaBreachFilter === 'no') params.slaBreach = false;
    if (selectedTag !== 'all') {
      const tagId = parseInt(selectedTag, 10);
      if (!Number.isNaN(tagId)) params.tagId = tagId;
    }
    
    const trimmedSearch = debouncedSearchTerm.trim();
    if (trimmedSearch) params.search = trimmedSearch;
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined || params[key] === '') delete params[key];
    });
    return params;
  };

  const loadReportStats = async () => {
    const fetchId = ++statsFetchIdRef.current;
    try {
      setChartDataLoading(true);
      const response = await ticketService.getListTicketsReportStatistics(buildReportFilterParams());
      if (fetchId !== statsFetchIdRef.current) return;
      const data = response?.data?.data || response?.data || {};
      setReportStats({
        totalTickets: Number(data.totalTickets) || 0,
        byStatus: Array.isArray(data.byStatus) ? data.byStatus : [],
        byPriority: Array.isArray(data.byPriority) ? data.byPriority : []
      });
    } catch (err) {
      if (fetchId !== statsFetchIdRef.current) return;
      console.warn('Report statistics load failed:', err);
      setReportStats({ totalTickets: 0, byStatus: [], byPriority: [] });
    } finally {
      if (fetchId === statsFetchIdRef.current) {
        setChartDataLoading(false);
      }
    }
  };

  // Statistics for charts/summary — full filtered dataset (not the current page)
  useEffect(() => {
    loadReportStats();
  }, [debouncedSearchTerm, selectedAgent, selectedStatus, selectedPriority, selectedCategory, selectedSource, fromDate, toDate, statuses, selectedInquiryType, selectedInquiryCategory, selectedInquirySubCategory, selectedInquiryPriority, selectedEscalationLevel, selectedResponsibleRole, escalatedFilter, slaBreachFilter, selectedTag]);

  // Agent options are fetched only for the chosen Responsible Role — no role selected, no users call.
  useEffect(() => {
    if (selectedResponsibleRole === 'all') {
      setAgents([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingAgents(true);
    (async () => {
      try {
        const users = await getAssignableUsers(selectedResponsibleRole);
        if (!cancelled) setAgents(users);
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to load agents:', error);
          setAgents([]);
        }
      } finally {
        if (!cancelled) setLoadingAgents(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedResponsibleRole]);

  // Keep Agent valid when the Responsible Role changes the user list.
  useEffect(() => {
    if (selectedAgent === 'all') return;
    if (!agents.some((u) => optionValue(u.userId ?? u.id) === selectedAgent)) setSelectedAgent('all');
  }, [agents, selectedAgent]);

  /** Agent is unusable until a role is picked, so say why rather than showing an empty list. */
  const assigneeHint = selectedResponsibleRole === 'all'
    ? 'Select a Responsible Role first'
    : loadingAgents
      ? 'Loading users…'
      : agents.length === 0 ? 'No active users in this role' : null;

  const loadTickets = async () => {
    const fetchId = ++ticketsFetchIdRef.current;
    try {
      setLoading(true);
      setError('');
      
      // Same filter set as statistics/export (buildReportFilterParams) + pagination
      const params = {
        page: currentPage,
        size: pageSize,
        ...buildReportFilterParams(),
      };

      const response = await ticketService.getAllTickets(params);
      if (fetchId !== ticketsFetchIdRef.current) return;
      
      const responseData = response?.data;
      
      let ticketsList = [];
      let total = 0;
      let pages = 0;
      
      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        
        // Handle paginated response structure
        if (paginatedData.content) {
          ticketsList = paginatedData.content;
          total = paginatedData.totalElements || 0;
          pages = paginatedData.totalPages || 0;
        } else if (Array.isArray(paginatedData)) {
          ticketsList = paginatedData;
          total = paginatedData.length;
          pages = 1;
        }
      } else if (Array.isArray(responseData)) {
        ticketsList = responseData;
        total = responseData.length;
        pages = 1;
      }
      
      const visibleTickets = excludeSuperAdminTickets(ticketsList);

      // Store fetched tickets
      setAllTickets(visibleTickets);
      setFilteredTickets(visibleTickets);

      // Store API pagination data
      setPaginationData({
        totalElements: total,
        totalPages: pages,
        pageNumber: currentPage,
        pageSize: pageSize,
        first: currentPage === 0,
        last: currentPage >= pages - 1
      });
    } catch (error) {
      if (fetchId !== ticketsFetchIdRef.current) return;
      console.error('Error loading tickets:', error);
      setError(extractErrorMessage(error, 'Failed to load tickets'));
      toast.error(extractErrorMessage(error, 'Failed to load tickets'));
      setAllTickets([]);
      setFilteredTickets([]);
      setPaginationData(null);
    } finally {
      if (fetchId === ticketsFetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  loadTicketsRef.current = loadTickets;
  loadReportStatsRef.current = loadReportStats;
  setChartDataLoadingRef.current = setChartDataLoading;

  useEffect(() => {
    const onRealtimeRefresh = () => {
      setChartDataLoadingRef.current?.(true);
      loadTicketsRef.current?.();
      loadReportStatsRef.current?.();
    };
    window.addEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
    return () => window.removeEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
  }, []);


  // Charts from dedicated statistics API (full filtered dataset)
  const pieChartData = useMemo(() => {
    return (reportStats.byStatus || [])
      .map((item) => ({
        name: String(item.name || 'Unknown').trim() || 'Unknown',
        value: Number(item.count) || 0
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [reportStats]);

  const handleViewTicket = (ticket) => {
    const ticketId = ticket?.ticketId || ticket?.id;
    if (!ticketId) {
      toast.error('Ticket ID not available');
      return;
    }
    navigate(`/admin/tickets/${ticketId}`, { state: { mode: 'view' } });
  };

  const barChartData = useMemo(() => {
    return (reportStats.byPriority || [])
      .map((item) => ({
        name: String(item.name || 'Unknown').trim() || 'Unknown',
        count: Number(item.count) || 0
      }))
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [reportStats]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && (paginationData ? newPage < paginationData.totalPages : true)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedAgent('all');
    setSelectedStatus('all');
    setSelectedPriority('all');
    setSelectedCategory('all');
    setSelectedSource('all');
    setFromDate('');
    setToDate('');
    setSelectedInquiryType('all');
    handleInquiryCategoryChange('all');
    setSelectedInquiryPriority('all');
    setSelectedEscalationLevel('all');
    setSelectedResponsibleRole('all');
    setEscalatedFilter('all');
    setSlaBreachFilter('all');
    setSelectedTag('all');
    setCurrentPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getPriorityBadgeColor = (priority) => {
    const priorityLower = (priority || '').toLowerCase();
    if (priorityLower.includes('critical')) return 'bg-red-100 text-red-800 border-red-300';
    if (priorityLower.includes('high')) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (priorityLower.includes('medium')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (priorityLower.includes('low')) return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusBadgeColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'open') return 'bg-blue-100 text-blue-800 border-blue-300';
    if (statusLower === 'in progress' || statusLower === 'in-progress') return 'bg-purple-100 text-purple-800 border-purple-300';
    if (statusLower === 'resolved') return 'bg-green-100 text-green-800 border-green-300';
    if (statusLower === 'closed') return 'bg-gray-100 text-gray-800 border-gray-300';
    if (statusLower === 'escalated') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getAgentName = (ticket) => {
    // First, check if ticket has assignee name directly
    if (ticket.assigneeName) {
      return ticket.assigneeName;
    }
    
    if (ticket.assignedAgentName) {
      return ticket.assignedAgentName;
    }
    
    // Check if ticket has assignee object with name
    if (ticket.assignee) {
      const name = ticket.assignee.fullName || ticket.assignee.name || ticket.assignee.username || ticket.assignee.userName;
      if (name) return name;
    }
    
    if (ticket.assignedAgent) {
      const name = ticket.assignedAgent.fullName || ticket.assignedAgent.name || ticket.assignedAgent.username || ticket.assignedAgent.userName;
      if (name) return name;
    }
    
    // Check multiple possible field names for assignee ID
    const assigneeId = ticket.assigneeId || ticket.assignedAgentId || ticket.assignedToId || ticket.assignedTo;
    
    if (assigneeId) {
      // The agent list is only loaded for a selected Responsible Role, so fall back to the id
      // instead of labelling an assigned ticket "Unassigned".
      const agent = agents.find((a) => String(a.userId ?? a.id) === String(assigneeId));
      return agent ? getUserDisplayName(agent) : `User ${assigneeId}`;
    }
    
    return 'Unassigned';
  };

  const hasActiveFilters = searchTerm || selectedAgent !== 'all' || selectedStatus !== 'all' || selectedPriority !== 'all' || selectedCategory !== 'all' || selectedSource !== 'all' || fromDate || toDate || selectedInquiryType !== 'all' || selectedInquiryCategory !== 'all' || selectedInquirySubCategory !== 'all' || selectedInquiryPriority !== 'all' || selectedEscalationLevel !== 'all' || selectedResponsibleRole !== 'all' || escalatedFilter !== 'all' || slaBreachFilter !== 'all' || selectedTag !== 'all';

  const buildExportParams = (page, size) => {
    const params = { page, size };
    if (selectedStatus !== 'all') {
      const selectedStatusObj = statuses.find(s => (s.statusCode || s.code || String(s.statusId || s.id)) === selectedStatus);
      params.status = selectedStatusObj?.statusCode || selectedStatusObj?.code || selectedStatus;
    }
    if (selectedPriority !== 'all') {
      const priorityId = parseInt(selectedPriority);
      if (!isNaN(priorityId)) params.priority = priorityId;
    }
    if (selectedCategory !== 'all') {
      const categoryId = parseInt(selectedCategory);
      if (!isNaN(categoryId)) params.categoryId = categoryId;
    }
    if (selectedAgent !== 'all') {
      const agentId = parseInt(selectedAgent);
      if (!isNaN(agentId)) params.assigneeId = agentId;
    }
    if (selectedSource !== 'all') params.source = selectedSource;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    
    // Inquiry filters
    if (selectedInquiryType !== 'all') {
      const typeId = parseInt(selectedInquiryType);
      if (!isNaN(typeId)) params.inquiryTypeId = typeId;
    }
    if (selectedInquiryCategory !== 'all') {
      const catId = parseInt(selectedInquiryCategory);
      if (!isNaN(catId)) params.inquiryCategoryId = catId;
    }
    if (selectedInquirySubCategory !== 'all') {
      const subCatId = parseInt(selectedInquirySubCategory);
      if (!isNaN(subCatId)) params.inquirySubCategoryId = subCatId;
    }
    if (selectedInquiryPriority !== 'all') {
      const priId = parseInt(selectedInquiryPriority);
      if (!isNaN(priId)) params.inquiryPriorityLevelId = priId;
    }
    if (selectedEscalationLevel !== 'all') {
      const escalationId = parseInt(selectedEscalationLevel);
      if (!isNaN(escalationId)) params.inquiryEscalationLevelId = escalationId;
    }
    if (selectedResponsibleRole !== 'all') {
      const roleId = parseInt(selectedResponsibleRole);
      if (!isNaN(roleId)) params.responsibleRoleId = roleId;
    }
    if (escalatedFilter === 'yes') params.escalated = true;
    else if (escalatedFilter === 'no') params.escalated = false;
    if (slaBreachFilter === 'yes') params.slaBreach = true;
    else if (slaBreachFilter === 'no') params.slaBreach = false;
    if (selectedTag !== 'all') {
      const tagId = parseInt(selectedTag, 10);
      if (!Number.isNaN(tagId)) params.tagId = tagId;
    }
    
    if (debouncedSearchTerm) params.search = debouncedSearchTerm;
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined || params[key] === '') delete params[key];
    });
    return params;
  };

  const fetchAllTicketsForExport = async () => {
    const first = await ticketService.getAllTickets(buildExportParams(0, 10));
    const responseData = first?.data;
    let total = 0;
    if (responseData?.success && responseData?.data) {
      const pd = responseData.data;
      if (pd.content) total = pd.totalElements || 0;
      else if (Array.isArray(pd)) total = pd.length;
    } else if (Array.isArray(responseData)) total = responseData.length;
    if (total <= 0) return [];
    const full = await ticketService.getAllTickets(buildExportParams(0, total));
    const fd = full?.data;
    if (fd?.success && fd?.data?.content) return fd.data.content;
    if (fd?.success && Array.isArray(fd?.data)) return fd.data;
    if (Array.isArray(fd)) return fd;
    return [];
  };

  const escapeCsvCell = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const allTickets = await fetchAllTicketsForExport();
      if (allTickets.length === 0) {
        toast.error('No data to export');
        return;
      }
      const headers = ['S.No', 'Ticket ID', 'Ticket Title', 'Inquiry Type', 'Category', 'Sub Category', 'Priority', 'Source', 'Agent Name', 'Status', 'Tags', 'Escalation Level', 'Responsible Role', 'Escalated', 'Current SLA', 'Created Date & Time', 'Resolved Date & Time', 'Closed Date & Time', 'Due Date'];
      const rows = allTickets.map((ticket, i) => {
        const cat = resolveTicketCategoryLabel(ticket);
        const pri = resolveTicketPriorityLabel(ticket);
        const st = ticket.status?.statusName || ticket.statusName || ticket.status || 'N/A';
        const agent = ticket.assigneeFullName || getAgentName(ticket) || 'unassigned';
        const dueDate = ticket.dueDate || ticket.slaDueDate || ticket.sla?.dueDate || '';
        const inquiryType = ticket.inquiryTypeName || '—';
        const inquirySubCategory = ticket.inquirySubCategoryName || '—';
        const escalationLevel = ticket.escalationLevelName || '—';
        const responsibleRole = ticket.responsibleRoleName || '—';
        const escalated = ticket.escalated === true ? 'Yes' : 'No';
        const currentSla = resolveCurrentSlaWindow(ticket, 0, statuses)?.label || '—';
        const tagsLabel = formatTicketTagsLabel(ticket, '');
        return [i + 1, ticket.ticketNumber || ticket.ticketId || 'N/A', ticket.subject || ticket.title || 'N/A', inquiryType, cat, inquirySubCategory, pri, ticket.source || 'Portal', agent, st, tagsLabel, escalationLevel, responsibleRole, escalated, currentSla, formatDateTime(ticket.createdAt || ticket.createdDate), formatDateTime(ticket.resolvedAt || ''), formatDateTime(ticket.closedAt || ''), dueDate ? formatDateTime(dueDate) : 'N/A'].map(escapeCsvCell);
      });
      const csv = [headers.map(escapeCsvCell).join(','), ...rows.map((r) => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `list-of-tickets-report-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported as CSV');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const allTickets = await fetchAllTicketsForExport();
      if (allTickets.length === 0) {
        toast.error('No data to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('List of Tickets Report', 14, 15);
      doc.setFontSize(10);
      const body = allTickets.map((ticket, i) => {
        const cat = resolveTicketCategoryLabel(ticket);
        const pri = resolveTicketPriorityLabel(ticket);
        const st = ticket.status?.statusName || ticket.statusName || ticket.status || 'N/A';
        const agent = ticket.assigneeFullName || getAgentName(ticket) || 'unassigned';
        const inquiryType = (ticket.inquiryTypeName || '—').substring(0, 12);
        const inquirySubCat = (ticket.inquirySubCategoryName || '—').substring(0, 12);
        const escalationLevel = (ticket.escalationLevelName || '—').substring(0, 10);
        const responsibleRole = (ticket.responsibleRoleName || '—').substring(0, 10);
        const escalated = ticket.escalated === true ? 'Yes' : 'No';
        const currentSla = (resolveCurrentSlaWindow(ticket, 0, statuses)?.label || '—').substring(0, 15);
        const tagsLabel = formatTicketTagsLabel(ticket, '');
        return [i + 1, ticket.ticketNumber || ticket.ticketId || 'N/A', (ticket.subject || ticket.title || 'N/A').substring(0, 25), inquiryType, cat.substring(0, 12), inquirySubCat, pri.substring(0, 8), ticket.source || 'Portal', agent.substring(0, 12), st.substring(0, 10), tagsLabel.substring(0, 16), escalationLevel, responsibleRole, escalated, currentSla];
      });
      autoTable(doc, {
        startY: 22,
        head: [['S.No', 'ID', 'Title', 'Type', 'Cat', 'SubCat', 'Pri', 'Src', 'Agent', 'Status', 'Tags', 'Esc.Lvl', 'Role', 'Esc', 'SLA']],
        body,
        styles: { fontSize: 5 }
      });
      doc.save(`list-of-tickets-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground">List of Tickets Report</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and filter all tickets with detailed information
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowFilters((prev) => !prev);
              setExportMenuOpen(false);
            }}
            className="flex items-center gap-2"
            aria-expanded={showFilters}
          >
            <Filter className="h-4 w-4" />
            Filter
            {hasActiveFilters && !showFilters && (
              <span className="ml-1 h-2 w-2 rounded-full bg-primary" aria-hidden />
            )}
          </Button>
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={loading || exporting}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[140px]">
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportCsv}>
                  <FileText className="h-4 w-4" /> Export CSV
                </button>
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportPdf}>
                  <FileText className="h-4 w-4" /> Export PDF
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={() => {
              loadTickets();
              loadReportStats();
            }}
            disabled={loading}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters Card (toggle via Filter button) */}
      {showFilters && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* All Filters in Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search Filter - Reduced size */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by ticket ID, title..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* From Date Filter - Moved after search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                From Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={fromDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    // Validate: don't allow future dates
                    const today = new Date().toISOString().split('T')[0];
                    if (selectedDate > today) {
                      toast.error('From Date cannot be in the future');
                      return;
                    }
                    // Validate: fromDate should not be after toDate
                    if (toDate && selectedDate > toDate) {
                      toast.error('From Date cannot be after To Date');
                      return;
                    }
                    setFromDate(selectedDate);
                    setCurrentPage(0);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* To Date Filter - Moved after search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                To Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    // Validate: don't allow future dates
                    const today = new Date().toISOString().split('T')[0];
                    if (selectedDate > today) {
                      toast.error('To Date cannot be in the future');
                      return;
                    }
                    // Validate: toDate should not be before fromDate
                    if (fromDate && selectedDate < fromDate) {
                      toast.error('To Date cannot be before From Date');
                      return;
                    }
                    setToDate(selectedDate);
                    setCurrentPage(0);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Agent Filter */}
            {/* <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Agent
              </label>
              <Select
                value={selectedAgent}
                onValueChange={(value) => {
                  setSelectedAgent(value);
                  setCurrentPage(0);
                }}
                disabled={loadingAgents || agents.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Agents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {agents.map((agent) => (
                    <SelectItem
                      key={agent.userId ?? agent.id}
                      value={optionValue(agent.userId ?? agent.id)}
                    >
                      {getUserDisplayName(agent)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {assigneeHint && (
                <p className="mt-1 text-xs text-muted-foreground">{assigneeHint}</p>
              )}
            </div> */}

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <Select
                value={selectedStatus}
                onValueChange={(value) => {
                  setSelectedStatus(value);
                  setCurrentPage(0);
                }}
                disabled={loadingStatuses}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statuses.map((status) => {
                    // Backend expects statusCode
                    let statusValue = status.statusCode || status.code;
                    
                    if (!statusValue && status.statusName) {
                      statusValue = status.statusName.toLowerCase().replace(/\s+/g, '-');
                    }
                    
                    if (!statusValue) {
                      statusValue = String(status.statusId || status.id);
                    }
                    
                    const statusDisplay = status.statusName || status.name || 'Unknown';
                    return (
                      <SelectItem
                        key={status.statusId || status.id}
                        value={statusValue}
                      >
                        {statusDisplay}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Priority
              </label>
              <Select
                value={selectedPriority}
                onValueChange={(value) => {
                  setSelectedPriority(value);
                  setCurrentPage(0);
                }}
                disabled={loadingPriorities}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {priorities.map((priority) => {
                    // Use priorityId as the value (number)
                    const priorityId = priority.priorityId || priority.id;
                    const priorityValue = priorityId ? String(priorityId) : 'all';
                    const priorityDisplay = priority.priorityName || priority.name || 'Unknown';
                    
                    return (
                      <SelectItem
                        key={priorityId || `priority-${priority.priorityName}`}
                        value={priorityValue}
                      >
                        {priorityDisplay}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category
              </label>
              <Select
                value={selectedCategory}
                onValueChange={(value) => {
                  setSelectedCategory(value);
                  setCurrentPage(0);
                }}
                disabled={loadingCategories}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem
                      key={category.categoryId || category.id}
                      value={String(category.categoryId || category.id)}
                    >
                      {category.categoryName || category.name || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Source
              </label>
              <Select
                value={selectedSource}
                onValueChange={(value) => {
                  setSelectedSource(value);
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="portal">Portal</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">whatsapp</SelectItem>
                  <SelectItem value="chatbot">Chatbot</SelectItem>
                  {/* <SelectItem value="api">API</SelectItem> */}
                </SelectContent>
              </Select>
            </div>

            {/* Tag Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Tag
              </label>
              <Select
                value={selectedTag}
                onValueChange={(value) => {
                  setSelectedTag(value);
                  setCurrentPage(0);
                }}
                disabled={loadingTags}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Tags" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {ticketTags.map((tag) => (
                    <SelectItem
                      key={tag.tagId ?? tag.id}
                      value={String(tag.tagId ?? tag.id)}
                    >
                      {tag.tagName || tag.name || 'Tag'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Type Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Type
              </label>
              <Select
                value={selectedInquiryType}
                onValueChange={handleInquiryTypeChange}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Inquiry Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Inquiry Types</SelectItem>
                  {inquiryTypes.map((type) => (
                    <SelectItem key={type.id} value={optionValue(type.id)}>
                      {type.inquiryTypeName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Category
              </label>
              <Select
                value={selectedInquiryCategory}
                onValueChange={handleInquiryCategoryChange}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Inquiry Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Inquiry Categories</SelectItem>
                  {visibleInquiryCategories.map((cat) => (
                    <SelectItem key={cat.id} value={optionValue(cat.id)}>
                      {cat.inquiryCategoryName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Sub Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Sub Category
              </label>
              <Select
                value={selectedInquirySubCategory}
                onValueChange={(value) => {
                  setSelectedInquirySubCategory(value);
                  setCurrentPage(0);
                }}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Sub Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sub Categories</SelectItem>
                  {inquirySubCategories.map((subCat) => (
                    <SelectItem key={subCat.id} value={optionValue(subCat.id)}>
                      {subCat.inquirySubCategoryName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Priority
              </label>
              <Select
                value={selectedInquiryPriority}
                onValueChange={(value) => {
                  setSelectedInquiryPriority(value);
                  setCurrentPage(0);
                }}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Inquiry Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Inquiry Priorities</SelectItem>
                  {inquiryPriorityLevels.map((pri) => (
                    <SelectItem key={pri.id} value={optionValue(pri.id)}>
                      {pri.priorityLevelName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Escalation Level Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Escalation Level
              </label>
              <Select
                value={selectedEscalationLevel}
                onValueChange={(value) => {
                  setSelectedEscalationLevel(value);
                  setCurrentPage(0);
                }}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Escalation Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Escalation Levels</SelectItem>
                  {escalationLevels.map((level) => (
                    <SelectItem key={level.id} value={optionValue(level.id)}>
                      {level.escalationLevelName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Responsible Role Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Responsible Role
              </label>
              <Select
                value={selectedResponsibleRole}
                onValueChange={(value) => {
                  setSelectedResponsibleRole(value);
                  setCurrentPage(0);
                }}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {responsibleRoles.map((role) => (
                    <SelectItem key={role.roleId} value={optionValue(role.roleId)}>
                      {role.label || role.roleCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Escalated Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Escalated
              </label>
              <Select
                value={escalatedFilter}
                onValueChange={(value) => {
                  setEscalatedFilter(value);
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* SLA Breach Filter */}
            {/* <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                SLA Breach
              </label>
              <Select
                value={slaBreachFilter}
                onValueChange={(value) => {
                  setSlaBreachFilter(value);
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div> */}

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="lg:col-span-4 flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Charts: Pie (by status) and Bar (by priority) */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 [&>*]:min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Tickets by status
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Distribution of tickets by status (filtered list)
              </p>
            </CardHeader>
            <CardContent>
              {chartDataLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3" />
                  <p className="text-sm">Loading chart...</p>
                </div>
              ) : pieChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display. Apply filters and ensure tickets are loaded.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="45%"
                      innerRadius={56}
                      outerRadius={88}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(name) => name}
                    />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      wrapperStyle={{ fontSize: '12px' }}
                      formatter={(value, entry) => (
                        <span className="text-foreground">
                          {value} {entry.payload?.value != null ? `(${entry.payload.value})` : ''}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Tickets by priority
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Priority (Inquiry Priority when present, else legacy)
              </p>
            </CardHeader>
            <CardContent>
              {chartDataLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3" />
                  <p className="text-sm">Loading chart...</p>
                </div>
              ) : barChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display. Apply filters and ensure tickets are loaded.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={barChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} className="text-xs" />
                    <Tooltip
                      formatter={(value) => [value, 'Count']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill={CHART_COLORS[0]} name="Tickets" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tickets Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Tickets
            {paginationData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({paginationData.totalElements} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading tickets...</span>
            </div>
           ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tickets found</p>
            </div>
          ) : (
            <>
              <div className="w-full min-w-0 overflow-x-auto rounded-md border border-border">
                {/* min-width = sum of the colgroup widths, so columns keep their size and the
                    table scrolls inside this container instead of stretching the page. */}
                <table className="w-full min-w-[1864px] table-fixed border-collapse">
                  <colgroup>
                    <col className="w-14" />
                    <col className="w-28" />
                    <col className="w-28" />
                    <col className="w-64" />
                    <col className="w-36" />
                    <col className="w-36" />
                    <col className="w-36" />
                    <col className="w-20" />
                    <col className="w-28" />
                    <col className="w-36" />
                    <col className="w-28" />
                    <col className="w-28" />
                    <col className="w-28" />
                    <col className="w-28" />
                    <col className="w-28" />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">S.No</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Ticket ID</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Requester Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Ticket Title</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Category</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Escalation</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Priority</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Source</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Agent Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Status</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Tags</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Created Date &amp; Time</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Resolved Date &amp; Time</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Closed Date &amp; Time</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Due Date</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground align-bottom leading-snug">Action</th>
                    </tr>
                  </thead>
                   <tbody>
                     {filteredTickets.map((ticket, index) => {
                      const requesterName = getRequesterDisplayName(ticket);
                      return (
                      <tr
                        key={ticket.ticketId || ticket.id}
                        className="border-b border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-3 text-sm text-foreground align-top">
                          {currentPage * pageSize + index + 1}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top whitespace-nowrap">
                          #{ticket.ticketNumber || ticket.ticketId || ticket.id}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="truncate block" title={requesterName}>
                            {requesterName}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <p
                            className="font-medium line-clamp-2 leading-snug break-words"
                            title={ticket.subject || ticket.title || 'N/A'}
                          >
                            {ticket.subject || ticket.title || 'N/A'}
                          </p>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span
                            className="block truncate"
                            title={resolveTicketCategoryLabel(ticket)}
                          >
                            {resolveTicketCategoryLabel(ticket)}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          {(() => {
                            const escalationLabel = [ticket.escalationLevelName, ticket.responsibleRoleName]
                              .filter(Boolean)
                              .join(' · ');
                            if (!escalationLabel) {
                              return <span className="text-muted-foreground">—</span>;
                            }
                            return (
                              <span className="block truncate" title={escalationLabel}>
                                {escalationLabel}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="p-3 align-top whitespace-nowrap">
                          <Badge
                            className={`${getPriorityBadgeColor(
                              resolveTicketPriorityLabel(ticket)
                            )} border whitespace-nowrap`}
                          >
                            {resolveTicketPriorityLabel(ticket)}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-foreground capitalize align-top whitespace-nowrap">
                          {ticket.source || 'Portal'}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="truncate block" title={ticket.assigneeFullName || 'unassigned'}>
                            {ticket.assigneeFullName || 'unassigned'}
                          </span>
                        </td>
                        <td className="p-3 align-top">
                          <Badge
                            className={`${getStatusBadgeColor(
                              ticket.status?.statusName || ticket.statusName || ticket.status
                            )} border max-w-full whitespace-normal text-center leading-tight`}
                          >
                            {ticket.status?.statusName || ticket.statusName || ticket.status || 'N/A'}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="line-clamp-2 leading-snug break-words" title={formatTicketTagsLabel(ticket)}>
                            {formatTicketTagsLabel(ticket)}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          {renderDateTimeCell(ticket.createdAt || ticket.createdDate)}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          {renderDateTimeCell(ticket.resolvedAt || '')}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          {renderDateTimeCell(ticket.closedAt || '')}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          {ticket.dueDate ? renderDateTimeCell(ticket.dueDate) : 'N/A'}
                        </td>
                        <td className="p-3 text-sm align-top whitespace-nowrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="inline-flex items-center gap-1.5 shrink-0"
                            onClick={() => handleViewTicket(ticket)}
                          >
                            <Eye className="h-4 w-4 shrink-0" />
                            View
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {paginationData && paginationData.totalElements > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-border gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to{' '}
                    {Math.min((currentPage + 1) * pageSize, paginationData.totalElements)} of{' '}
                    {paginationData.totalElements} tickets
                  </div>
                  <div className="flex items-center gap-2">
                    {/* First Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(0)}
                        disabled={currentPage === 0 || loading}
                        className="hidden sm:flex"
                      >
                        First
                      </Button>
                    )}
                    
                    {/* Previous Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                    )}
                    
                    {/* Page Numbers */}
                    {paginationData.totalPages > 0 && (
                      <div className="flex items-center gap-1">
                        {(() => {
                          const pages = [];
                          const totalPages = paginationData.totalPages;
                          const current = currentPage + 1;
                          
                          // Show page numbers with ellipsis
                          if (totalPages <= 7) {
                            // Show all pages if 7 or fewer
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(
                                <Button
                                  key={i}
                                  variant={i === current ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handlePageChange(i - 1)}
                                  disabled={loading || totalPages === 1}
                                  className="min-w-[2.5rem]"
                                >
                                  {i}
                                </Button>
                              );
                            }
                          } else {
                            // Show first page
                            pages.push(
                              <Button
                                key={1}
                                variant={current === 1 ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(0)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                1
                              </Button>
                            );
                            
                            // Show ellipsis if current page is far from start
                            if (current > 3) {
                              pages.push(
                                <span key="ellipsis-start" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            // Show pages around current page
                            const start = Math.max(2, current - 1);
                            const end = Math.min(totalPages - 1, current + 1);
                            
                            for (let i = start; i <= end; i++) {
                              if (i !== 1 && i !== totalPages) {
                                pages.push(
                                  <Button
                                    key={i}
                                    variant={i === current ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handlePageChange(i - 1)}
                                    disabled={loading}
                                    className="min-w-[2.5rem]"
                                  >
                                    {i}
                                  </Button>
                                );
                              }
                            }
                            
                            // Show ellipsis if current page is far from end
                            if (current < totalPages - 2) {
                              pages.push(
                                <span key="ellipsis-end" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            // Show last page
                            pages.push(
                              <Button
                                key={totalPages}
                                variant={current === totalPages ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                {totalPages}
                              </Button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                    )}
                    
                    {/* Next Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}
                    
                    {/* Last Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(paginationData.totalPages - 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                        className="hidden sm:flex"
                      >
                        Last
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ListOfTicketsReport;

